import { useState } from "react";
import type { CheckResponse, DesiredTone } from "../types/api";

type RewriteCardProps = {
  response: CheckResponse;
  onAdjust: (tone: DesiredTone) => void;
  onClientEvent: (event: "rewrite_copied" | "result_shared") => void;
};

const adjustments: Array<{ tone: DesiredTone; label: string }> = [
  { tone: "shorter", label: "shorter" },
  { tone: "warmer", label: "warmer" },
  { tone: "more_casual", label: "more casual" },
  { tone: "more_professional", label: "more professional" }
];

async function copyText(text: string) {
  if (navigator.clipboard) {
    await navigator.clipboard.writeText(text);
    return;
  }
  const textarea = document.createElement("textarea");
  textarea.value = text;
  textarea.setAttribute("readonly", "true");
  textarea.style.position = "fixed";
  textarea.style.left = "-9999px";
  document.body.appendChild(textarea);
  textarea.select();
  document.execCommand("copy");
  textarea.remove();
}

export function RewriteCard({ response, onAdjust, onClientEvent }: RewriteCardProps) {
  const [copied, setCopied] = useState(false);
  if (!response.result.rewrite.available) return null;

  async function handleCopy() {
    await copyText(response.result.rewrite.text);
    setCopied(true);
    onClientEvent("rewrite_copied");
    window.setTimeout(() => setCopied(false), 1800);
  }

  return (
    <section className="rewrite-card" aria-labelledby="rewrite-heading">
      <div className="section-heading-row">
        <h3 id="rewrite-heading">Less-unc version</h3>
        <button type="button" className="secondary-button" onClick={handleCopy}>
          {copied ? "Copied ✨" : "Copy rewrite"}
        </button>
      </div>
      <p className="rewrite-text">{response.result.rewrite.text}</p>
      {response.result.rewrite.rationale ? <p className="muted">{response.result.rewrite.rationale}</p> : null}
      <div className="chip-row" aria-label="Rewrite adjustments">
        {adjustments.map((adjustment) => (
          <button key={adjustment.tone} type="button" className="chip-button" onClick={() => onAdjust(adjustment.tone)}>
            {adjustment.label}
          </button>
        ))}
      </div>
      <span className="sr-only" aria-live="polite">
        {copied ? "Rewrite copied." : ""}
      </span>
    </section>
  );
}
